import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../api/client';
import { carUrl } from '../lib/slugId';
import { PageHeader } from '../components/PageHeader';
import type { Car, Conversation, Paginated } from '../api/types';

interface CarGroup {
  car: Car;
  conversations: Conversation[];
  unreadTotal: number;
  lastActivityAt: string;
}

export function Inbox() {
  const { t } = useTranslation();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    apiFetch<Paginated<Conversation>>('/api/v1/conversations?per_page=50')
      .then((res) => setConversations(res.data))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const groups: CarGroup[] = useMemo(() => {
    const byCarId = new Map<number, CarGroup>();
    for (const c of conversations) {
      if (!c.car) continue;
      const existing = byCarId.get(c.car.id);
      if (existing) {
        existing.conversations.push(c);
        existing.unreadTotal += c.unread_count;
        if (
          c.last_message_at &&
          (!existing.lastActivityAt || c.last_message_at > existing.lastActivityAt)
        ) {
          existing.lastActivityAt = c.last_message_at;
        }
      } else {
        byCarId.set(c.car.id, {
          car: c.car,
          conversations: [c],
          unreadTotal: c.unread_count,
          lastActivityAt: c.last_message_at ?? c.created_at,
        });
      }
    }
    // Sort each group's conversations newest first
    const groupsArray: CarGroup[] = [];
    byCarId.forEach((group) => {
      group.conversations.sort((a: Conversation, b: Conversation) => {
        const aTs = a.last_message_at ?? a.created_at;
        const bTs = b.last_message_at ?? b.created_at;
        return bTs.localeCompare(aTs);
      });
      groupsArray.push(group);
    });
    // Sort groups by most recent activity
    groupsArray.sort((a: CarGroup, b: CarGroup) =>
      b.lastActivityAt.localeCompare(a.lastActivityAt)
    );
    return groupsArray;
  }, [conversations]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 4 } }}>
      <PageHeader crumbs={[{ label: t('inbox.title') }]} />
      <Stack spacing={4}>
        <Typography variant="h3">{t('inbox.title')}</Typography>

        {groups.length === 0 ? (
          <Typography color="text.secondary">{t('inbox.empty')}</Typography>
        ) : (
          groups.map((group) => (
            <Stack key={group.car.id} spacing={1.5}>
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                component={RouterLink}
                to={carUrl(group.car)}
                sx={{ textDecoration: 'none', color: 'inherit' }}
              >
                {group.car.photo_urls[0] && (
                  <Avatar
                    variant="rounded"
                    src={group.car.photo_urls[0]}
                    sx={{ width: 56, height: 40 }}
                  />
                )}
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="h6" noWrap>
                    {group.car.display_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {group.car.year} {group.car.make} {group.car.model}
                  </Typography>
                </Box>
                {group.unreadTotal > 0 && (
                  <Chip
                    size="small"
                    color="error"
                    label={t('inbox.unread', { count: group.unreadTotal })}
                  />
                )}
              </Stack>
              <Divider />
              <Stack spacing={1.5}>
                {group.conversations.map((c) => (
                  <Card key={c.id} variant="outlined">
                    <CardActionArea component={RouterLink} to={`/inbox/${c.id}`}>
                      <CardContent>
                        <Stack direction="row" spacing={2} alignItems="flex-start">
                          <Avatar src={c.other_participant?.avatar_url ?? undefined}>
                            {c.other_participant?.name?.[0] ?? '?'}
                          </Avatar>
                          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                              spacing={1}
                            >
                              <Typography variant="subtitle1" fontWeight={600} noWrap>
                                {c.other_participant?.name ?? '—'}
                              </Typography>
                              {c.unread_count > 0 && (
                                <Chip
                                  size="small"
                                  color="error"
                                  label={t('inbox.unread', { count: c.unread_count })}
                                />
                              )}
                            </Stack>
                            {c.latest_message && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  mt: 0.5,
                                }}
                              >
                                {c.latest_message.body}
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                ))}
              </Stack>
            </Stack>
          ))
        )}
      </Stack>
    </Container>
  );
}
